import { useSelector, useDispatch } from 'react-redux';
import {
  useGetUserPersonImagesQuery,
  useGetLatestPersonImageQuery,
  useUploadPersonImageMutation,
  useSetActivePersonImageMutation,
  useDeletePersonImageMutation,
  useUpdatePersonImageMutation
} from '../redux/apis/endpoints/personImageApi';
import {
  setActivePersonImage,
  clearActivePersonImage,
  updatePosition,
  movePosition,
  centerImage,
  updateScale,
  scaleBy,
  resetScale,
  updateCanvasSize,
  setVirtualTryOnResult,
  revertToOriginal,
  setFilters,
  setSortBy,
  setShowActive,
  setLoading,
  setUploadProgress,
  setError,
  clearError,
  updateSettings,
  resetPersonImageState,
  setCanvasIntegrationMode
} from '../redux/slices/personImageSlice';

/**
 * Custom hook that combines RTK Query person image data with UI state management
 * Provides a unified interface for person image operations
 */
export const usePersonImages = () => {
  const dispatch = useDispatch();

  // Get UI state from slice with safe defaults
  const activePersonImageId = useSelector(state => state.personImage?.activePersonImageId);
  const position = useSelector(state => state.personImage?.position || { x: 0, y: 0 });
  const scale = useSelector(state => state.personImage?.scale || 1.0);
  const isVirtualTryOnResult = useSelector(state => state.personImage?.isVirtualTryOnResult || false);
  const virtualTryOnMetadata = useSelector(state => state.personImage?.virtualTryOnMetadata);
  const canvasSize = useSelector(state => state.personImage?.canvasSize || { width: 800, height: 600 });
  const settings = useSelector(state => state.personImage?.settings || {
    autoCenter: true,
    autoScale: true,
    preserveAspectRatio: true,
    maxScale: 2.0,
    minScale: 0.1
  });
  const filters = useSelector(state => state.personImage?.filters || {
    sortBy: 'created_at:desc',
    showActive: true
  });
  const isCanvasIntegrated = useSelector(state => state.personImage?.isCanvasIntegrated || false);
  const isLoading = useSelector(state => state.personImage?.isLoading || false);
  const uploadProgress = useSelector(state => state.personImage?.uploadProgress || 0);
  const error = useSelector(state => state.personImage?.error);

  // Check authentication before making API calls
  const token = localStorage.getItem('token');
  const isAuthenticated = !!token;

  // Get person images data from RTK Query
  // Skip the query if filters are not ready or user is not authenticated
  const {
    data: personImagesData,
    isLoading: isLoadingImages,
    error: imagesError,
    refetch: refetchImages
  } = useGetUserPersonImagesQuery({
    active: filters?.showActive ?? true,
    sort: filters?.sortBy ?? 'created_at:desc'
  }, {
    skip: !filters || !isAuthenticated, // Skip query if filters are undefined or not authenticated
    // Allow refetching when cache is invalidated (e.g., after background removal)
    // but prevent automatic refetching on mount/focus to avoid spamming
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    refetchOnReconnect: false
    // Note: Tag invalidation will still trigger refetch automatically
  });

  // Get latest person image
  const {
    data: latestPersonImage,
    isLoading: isLoadingLatest,
    error: latestError,
    refetch: refetchLatest
  } = useGetLatestPersonImageQuery(undefined, {
    skip: !isAuthenticated, // Skip query if not authenticated
    // Prevent automatic refetching on errors
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    refetchOnReconnect: false
  });

  // RTK Query mutations
  const [uploadPersonImage, { isLoading: isUploading }] = useUploadPersonImageMutation();
  const [setActivePersonImageAPI, { isLoading: isSettingActive }] = useSetActivePersonImageMutation();
  const [deletePersonImage, { isLoading: isDeleting }] = useDeletePersonImageMutation();
  const [updatePersonImage, { isLoading: isUpdating }] = useUpdatePersonImageMutation();

  // Log errors for debugging
  if (imagesError) {
    console.error('Error fetching person images:', imagesError);
  }
  if (latestError) {
    console.error('Error fetching latest person image:', latestError);
  }

  // Get current active person image data
  let activePersonImage = activePersonImageId
    ? personImagesData?.images?.find(img => String(img.id) === String(activePersonImageId))
    : null;

  // Fallback: If ID match failed, try to match by URL using VTO metadata
  // This handles cases where history items point to deleted person images but the image file still exists
  if (!activePersonImage && activePersonImageId && virtualTryOnMetadata?.originalPersonImage && personImagesData?.images) {
    const urlToMatch = virtualTryOnMetadata.originalPersonImage;
    activePersonImage = personImagesData.images.find(img =>
      img.image?.url === urlToMatch ||
      img.url === urlToMatch
    );

    if (activePersonImage) {
      // console.log('✅ Found matching person image by URL fallback:', activePersonImage.id);
    }
  }

  // Default fallback: prioritize default image over latest
  // If activePersonImage is null (e.g., stale ID that was deleted), we should fall back.
  if (!activePersonImage && personImagesData?.images?.length > 0) {
    // First try to find default image
    const defaultImage = personImagesData.images.find(img => img.isDefault);

    if (defaultImage) {
      activePersonImage = defaultImage;
      // console.log('✅ Using default person image as fallback:', defaultImage.id);
    } else if (latestPersonImage) {
      // Fall back to latest if no default
      activePersonImage = latestPersonImage;
      // console.log('✅ Using latest person image as fallback (no default set)');
    } else {
      // If latestPersonImage query isn't ready but we have images, just use the first one
      activePersonImage = personImagesData.images[0];
    }
  }

  // UI actions
  const handleSetActivePersonImage = (personImageId, imageDimensions) => {
    dispatch(setActivePersonImage({ personImageId, imageDimensions }));
  };

  const handleClearActivePersonImage = () => {
    dispatch(clearActivePersonImage());
  };

  // Position and scale actions
  const handleUpdatePosition = (position) => {
    dispatch(updatePosition(position));
  };

  const handleMovePosition = (deltaX, deltaY) => {
    dispatch(movePosition({ deltaX, deltaY }));
  };

  const handleCenterImage = () => {
    dispatch(centerImage());
  };

  const handleUpdateScale = (newScale) => {
    dispatch(updateScale(newScale));
  };

  const handleScaleBy = (factor) => {
    dispatch(scaleBy(factor));
  };

  const handleResetScale = () => {
    dispatch(resetScale());
  };

  // Canvas actions
  const handleUpdateCanvasSize = (width, height) => {
    dispatch(updateCanvasSize({ width, height }));
  };

  const handleSetCanvasIntegrationMode = (integrated) => {
    dispatch(setCanvasIntegrationMode(integrated));
  };

  // VTO actions
  const handleSetVirtualTryOnResult = (metadata) => {
    dispatch(setVirtualTryOnResult({ metadata }));
  };

  const handleRevertToOriginal = () => {
    dispatch(revertToOriginal());
  };

  // Filter actions
  const handleSetFilters = (newFilters) => {
    dispatch(setFilters(newFilters));
  };

  const handleSetSortBy = (sortBy) => {
    dispatch(setSortBy(sortBy));
  };

  const handleSetShowActive = (showActive) => {
    dispatch(setShowActive(showActive));
  };

  // Settings actions
  const handleUpdateSettings = (newSettings) => {
    dispatch(updateSettings(newSettings));
  };

  // API actions - setActivePersonImageAPI must be defined before uploadPersonImage
  const handleSetActivePersonImageAPI = async (personImageId) => {
    try {
      const result = await setActivePersonImageAPI(personImageId).unwrap();

      // Update local UI state with image URL for canvas display
      // Respect useRemovedImage flag: if true and removedBgImage exists, use it; otherwise use original
      const imageDimensions = result.image ? {
        width: result.image.width || 400,
        height: result.image.height || 600
      } : null;

      const shouldUseRemoved = result.useRemovedImage !== false && result.removedBgImage?.url;
      const imageUrl = shouldUseRemoved ? result.removedBgImage.url : (result.image?.url || null);

      dispatch(setActivePersonImage({
        personImageId,
        imageDimensions,
        imageUrl
      }));

      return result;
    } catch (error) {
      dispatch(setError(error.message || 'Failed to set active image'));
      throw error;
    }
  };

  const handleUploadPersonImage = async (uploadData) => {
    try {
      dispatch(setLoading(true));
      const result = await uploadPersonImage(uploadData).unwrap();

      // Always set newly uploaded image as active via API
      // This ensures the canvas is updated with the new image
      await handleSetActivePersonImageAPI(result.id);
      
      // Enable canvas integration so the image displays on canvas
      dispatch(setCanvasIntegrationMode(true));

      dispatch(setLoading(false));
      return result;
    } catch (error) {
      dispatch(setError(error.message || 'Upload failed'));
      throw error;
    }
  };

  const handleDeletePersonImage = async (personImageId) => {
    try {
      await deletePersonImage(personImageId).unwrap();

      // Clear active image if it was the deleted one
      if (String(activePersonImageId) === String(personImageId)) {
        handleClearActivePersonImage();
      }

      return true;
    } catch (error) {
      dispatch(setError(error.message || 'Delete failed'));
      throw error;
    }
  };

  const handleUpdatePersonImage = async (personImageId, updates) => {
    try {
      const result = await updatePersonImage({ id: personImageId, ...updates }).unwrap();
      return result;
    } catch (error) {
      dispatch(setError(error.message || 'Update failed'));
      throw error;
    }
  };

  // Helper functions
  const getPersonImageById = (personImageId) => {
    return personImagesData?.images?.find(img => img.id === personImageId);
  };

  const hasPersonImages = () => {
    return personImagesData?.images?.length > 0;
  };

  const getFilteredImages = () => {
    let images = personImagesData?.images || [];

    if (filters.showActive) {
      images = images.filter(img => img.isActive);
    }

    return images;
  };

  // Reset state
  const handleResetState = () => {
    dispatch(resetPersonImageState());
  };

  // Clear error
  const handleClearError = () => {
    dispatch(clearError());
  };

  return {
    // Data
    personImages: getFilteredImages(),
    allPersonImages: personImagesData?.images || [],
    activePersonImage,
    activePersonImageId: activePersonImage?.id || null,
    latestPersonImage,

    // UI State
    position,
    scale,
    isVirtualTryOnResult,
    virtualTryOnMetadata,
    canvasSize,
    settings,
    filters,
    isCanvasIntegrated,

    // Loading states
    isLoading: isLoading || isLoadingImages || isLoadingLatest,
    isUploading,
    isSettingActive,
    isDeleting,
    isUpdating,
    uploadProgress,
    error: error || imagesError || latestError,

    // Actions
    setActivePersonImage: handleSetActivePersonImageAPI,
    clearActivePersonImage: handleClearActivePersonImage,

    // Position and scale
    updatePosition: handleUpdatePosition,
    movePosition: handleMovePosition,
    centerImage: handleCenterImage,
    updateScale: handleUpdateScale,
    scaleBy: handleScaleBy,
    resetScale: handleResetScale,

    // Canvas
    updateCanvasSize: handleUpdateCanvasSize,
    setCanvasIntegrationMode: handleSetCanvasIntegrationMode,

    // VTO
    setVirtualTryOnResult: handleSetVirtualTryOnResult,
    revertToOriginal: handleRevertToOriginal,

    // Filters
    setFilters: handleSetFilters,
    setSortBy: handleSetSortBy,
    setShowActive: handleSetShowActive,

    // Settings
    updateSettings: handleUpdateSettings,

    // API actions
    uploadPersonImage: handleUploadPersonImage,
    deletePersonImage: handleDeletePersonImage,
    updatePersonImage: handleUpdatePersonImage,
    refetchImages,
    refetchLatest,

    // Helpers
    getPersonImageById,
    hasPersonImages,
    resetState: handleResetState,
    clearError: handleClearError
  };
};

export default usePersonImages;